import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import SocialButtons from "@/components/SocialButtons";
import { CONTACT } from "@/lib/api";

const PILLARS = [
  { label: "Loans & Funding", to: "/loans" },
  { label: "Wealth Portfolio", to: "/wealth" },
  { label: "Insurance Solutions", to: "/insurance" },
  { label: "Credit Card Bill Swiping", to: "/credit-card-liquidity" },
  { label: "Digital Marketing", to: "/digital-marketing" },
  { label: "IT Services", to: "/it-services" },
  { label: "Interior Design & Architecture", to: "/interior-design" },
  { label: "Videography & Photography", to: "/videography-photography" },
  { label: "Event Management", to: "/event-management" },
  { label: "Destination Weddings", to: "/destination-weddings" },
];

export default function Footer() {
  return (
    <footer className="grain relative overflow-hidden bg-navy text-white" data-testid="site-footer">
      <div className="container-x relative z-10 pb-10 pt-20 sm:pt-24">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo light />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/65">
              Evolve Elite is an Ahmedabad-based comprehensive consultancy delivering end-to-end solutions in commercial and retail funding, wealth management, IT infrastructure, digital marketing, and architectural design. We serve as a unified growth partner, empowering clients to secure capital, scale their brands online, and transform physical workspaces.
            </p>
            <SocialButtons dark className="mt-8" />
            <Link to="/contact" className="btn-gold mt-8" data-testid="footer-cta-link">
              Request a Callback <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Our Ten Pillars</p>
            <ul className="mt-5 space-y-2.5">
              {PILLARS.map((p) => (
                <li key={p.to}>
                  <Link to={p.to} className="text-sm font-bold text-white/75 transition hover:text-gold" data-testid={`footer-link-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">Head Office</p>
            <ul className="mt-5 space-y-4 text-sm text-white/75">
              <li>
                <a
                  href={CONTACT.mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-3 transition hover:text-gold"
                  data-testid="footer-address-link"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{CONTACT.address}</span>
                </a>
              </li>
              <li>
                <a href={CONTACT.phoneHref} className="flex gap-3 transition hover:text-gold" data-testid="footer-phone-link">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex gap-3 transition hover:text-gold" data-testid="footer-email-link">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex select-none justify-center overflow-hidden" aria-hidden="true">
          <p className="whitespace-nowrap bg-gradient-to-r from-white/10 via-gold/40 to-white/10 bg-clip-text text-center font-display text-[9.5vw] font-extrabold leading-none tracking-tight text-transparent">
            EVOLVE ELITE
          </p>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/45 md:flex-row">
          <p>© {new Date().getFullYear()} Evolve Elite · Comprehensive Consultancy · Ahmedabad, Gujarat</p>
          <div className="flex items-center gap-6">
            <Link to="/admin" className="transition hover:text-gold" data-testid="footer-admin-link">
              Lead Desk
            </Link>
            <span className="max-w-md md:text-right">
              Loans, insurance and investments are facilitated through partner institutions and are subject to their
              terms. Investments carry market risks — please read all scheme documents carefully.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
